import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useToast } from "@/components/Toast";
import { Badge, Button, Card, Empty } from "@/components/ui";
import { api, type LeadDetail } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

/**
 * §8.2's snapshot history.
 *
 * Two rules from §8.2 are visible in the markup rather than only in the prompt:
 *
 *  - **Every item is "unverified" until a person or a vendor engagement sets verified_by.** The
 *    badge is on every row for that reason; a researched value that looks like a confirmed one is
 *    how a diligence pack starts being trusted before anyone has checked it.
 *  - **The history is a history.** Newest first, older snapshots kept. What the research said last
 *    month is evidence about the parcel *and* about the research.
 *
 * Items with no source URL are called out rather than hidden: the schema refuses them, so one
 * appearing here at all would mean something wrote to the column outside the job.
 */

type SnapshotItem = {
  value?: string | number | boolean | null;
  confidence?: number;
  source_url?: string | null;
  retrieved_at?: string | null;
};

type Snapshot = {
  items?: Record<string, SnapshotItem>;
  kill_flags?: string[];
  suggested_checklist_edits?: string[];
  summary?: string;
  taken_at?: string;
  model?: string;
  prompt_hash?: string;
  verified_by?: string | null;
};

function label(key: string): string {
  return key.replace(/_/g, " ").replace(/\bpct\b/, "%");
}

function renderValue(value: SnapshotItem["value"]): string {
  if (value === null || value === undefined) return "not found";
  if (typeof value === "boolean") return value ? "yes" : "no";
  return String(value);
}

export function DdSnapshotTab({ lead }: { lead: LeadDetail }) {
  const qc = useQueryClient();
  const toast = useToast();
  const parcel = lead.parcels[0];

  const run = useMutation({
    mutationFn: () => {
      if (!parcel) throw new Error("this lead has no parcel to research");
      return api.ddSnapshot(parcel.id);
    },
    onSuccess: (result) => {
      if (result.status === "ok") toast.push("Snapshot added");
      else if (result.status === "refused_budget")
        toast.push("Refused: the monthly agent budget is spent", "danger");
      else toast.push(result.error ?? "Research failed", "danger");
      void qc.invalidateQueries({ queryKey: ["lead", lead.lead.id] });
    },
    onError: (e: Error) => {
      toast.push(e.message, "danger");
    },
  });

  const history = ((parcel?.dd_snapshot ?? []) as Snapshot[]).slice().reverse();

  const action = (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => run.mutate()}
      disabled={run.isPending || !parcel}
      data-testid="dd-run"
    >
      {run.isPending ? "Researching…" : "Research now"}
    </Button>
  );

  if (history.length === 0) {
    return (
      <Card title="Due-diligence snapshot" action={action} data-testid="dd-snapshot">
        <Empty
          title="No snapshot yet."
          hint="§8.2 researches zoning, floodplain, access and utilities from public sources. Every item it returns stays unverified until a person or a vendor engagement confirms it."
        />
      </Card>
    );
  }

  return (
    <Card
      title={`Due-diligence snapshot (${history.length})`}
      action={action}
      data-testid="dd-snapshot"
    >
      <div className="space-y-4">
        {history.map((snap, i) => (
          <section
            key={snap.taken_at ?? i}
            className="rounded border p-2"
            data-testid="dd-snapshot-entry"
          >
            <header className="flex flex-wrap items-center gap-2 text-[11px] text-fg-muted">
              <Badge tone={i === 0 ? "info" : "muted"}>
                {i === 0 ? "latest" : `older (#${history.length - i})`}
              </Badge>
              <Badge tone={snap.verified_by ? "ok" : "warn"} data-testid="dd-verified">
                {snap.verified_by ? `verified by ${snap.verified_by}` : "unverified"}
              </Badge>
              {snap.taken_at && <span>{formatDateTime(snap.taken_at)}</span>}
              {snap.model && <span className="ml-auto">{snap.model}</span>}
            </header>

            {snap.kill_flags && snap.kill_flags.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="dd-kill-flags">
                {snap.kill_flags.map((flag) => (
                  <li key={flag} className="rounded bg-danger/10 px-2 py-1 text-xs text-danger">
                    {flag}
                  </li>
                ))}
              </ul>
            )}

            {snap.items && Object.keys(snap.items).length > 0 && (
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-xs" data-testid="dd-items">
                  <thead>
                    <tr className="border-b text-left text-fg-muted">
                      <th className="py-1 pr-3 font-medium">Item</th>
                      <th className="py-1 pr-3 font-medium">Value</th>
                      <th className="py-1 pr-3 text-right font-medium">Confidence</th>
                      <th className="py-1 pr-3 font-medium">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(snap.items).map(([key, item]) => (
                      <tr key={key} className="border-b last:border-b-0">
                        <td className="py-1 pr-3">{label(key)}</td>
                        <td className="py-1 pr-3">{renderValue(item.value)}</td>
                        <td className="py-1 pr-3 text-right tabular-nums">
                          {typeof item.confidence === "number"
                            ? `${Math.round(item.confidence * 100)}%`
                            : "—"}
                        </td>
                        <td className="py-1 pr-3">
                          {item.source_url ? (
                            <a
                              className="underline"
                              href={item.source_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              source
                            </a>
                          ) : (
                            <span className="text-danger">no source</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {snap.summary && <p className="mt-2 text-xs text-fg-muted">{snap.summary}</p>}

            {snap.suggested_checklist_edits && snap.suggested_checklist_edits.length > 0 && (
              <ul className="mt-2 list-disc space-y-0.5 pl-4 text-xs text-fg-muted">
                {snap.suggested_checklist_edits.map((edit) => (
                  <li key={edit}>{edit}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </Card>
  );
}
